import { useCallback, useMemo, useState } from "react";

import { T1FilterValue } from "../components/T1Filter";
import type { Place } from "../types/warehouse.types";

export interface UseProductFiltersProps {
  allPlaces: Place[];
}

export interface UseProductFiltersResult {
  // Customer/Product filtering
  selectedCustomer: string | null;
  selectedProduct: string | null;
  handleCustomerChange: (value: string | null) => void;
  handleProductChange: (value: string | null) => void;
  availableProducts: string[];

  // Pallet Customer/Product filtering
  selectedPalletCustomer: string | null;
  selectedPalletProduct: string | null;
  handlePalletCustomerChange: (value: string | null) => void;
  handlePalletProductChange: (value: string | null) => void;
  availablePalletProducts: string[];
  selectedT1: T1FilterValue;
  handleT1Change: (value: T1FilterValue) => void;

  // Filter function to use in useMemo
  filterPlace: (place: Place) => boolean;
}

/**
 * Hook for managing customer and product filters.
 *
 * Handles both regular customer/product filters and pallet-specific filters.
 * Provides cascading filter logic (when customer is selected, only products
 * for that customer are available).
 *
 * @example
 * const { selectedCustomer, handleCustomerChange, filterPlace } =
 *   useProductFilters({ allPlaces });
 */
export const useProductFilters = ({
  allPlaces,
}: UseProductFiltersProps): UseProductFiltersResult => {
  // Regular customer/product state
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

  // Pallet customer/product state
  const [selectedPalletCustomer, setSelectedPalletCustomer] = useState<string | null>(null);
  const [selectedPalletProduct, setSelectedPalletProduct] = useState<string | null>(null);
  const [selectedT1, setSelectedT1] = useState<T1FilterValue>(null);

  // When customer changes, reset product selection
  const handleCustomerChange = useCallback((value: string | null) => {
    setSelectedCustomer(value);
    setSelectedProduct(null);
  }, []);

  const handleProductChange = useCallback((value: string | null) => {
    setSelectedProduct(value);
  }, []);

  // When pallet customer changes, reset pallet product selection
  const handlePalletCustomerChange = useCallback((value: string | null) => {
    setSelectedPalletCustomer(value);
    setSelectedPalletProduct(null);
  }, []);

  const handlePalletProductChange = useCallback((value: string | null) => {
    setSelectedPalletProduct(value);
  }, []);

  const handleT1Change = useCallback((value: T1FilterValue) => {
    setSelectedT1(value);
  }, []);

  // Calculate available products based on selected customer
  const availableProducts = useMemo(() => {
    if (!selectedCustomer) return [];

    const products = new Set<string>();
    allPlaces.forEach((place) => {
      if (place.customer_name === selectedCustomer && place.product_name) {
        products.add(place.product_name);
      }
    });
    return Array.from(products).sort();
  }, [selectedCustomer, allPlaces]);

  // Calculate available pallet products based on selected pallet customer
  const availablePalletProducts = useMemo(() => {
    if (!selectedPalletCustomer) return [];

    const products = new Set<string>();
    allPlaces.forEach((place) => {
      if (place.pallet_customer_name === selectedPalletCustomer && place.pallet_product_name) {
        products.add(place.pallet_product_name);
      }
    });
    return Array.from(products).sort();
  }, [selectedPalletCustomer, allPlaces]);

  // Filter function to use in filteredPlaces
  const filterPlace = useCallback(
    (place: Place): boolean => {
      if (selectedPalletCustomer && place.pallet_customer_name !== selectedPalletCustomer) {
        return false;
      }
      if (selectedPalletProduct && place.pallet_product_name !== selectedPalletProduct) {
        return false;
      }
      if (selectedCustomer && place.customer_name !== selectedCustomer) {
        return false;
      }
      if (selectedProduct && place.product_name !== selectedProduct) {
        return false;
      }
      if (selectedT1 !== null && place.pallet_is_t1 !== selectedT1) {
        return false;
      }
      return true;
    },
    [selectedCustomer, selectedProduct, selectedPalletCustomer, selectedPalletProduct, selectedT1]
  );

  return {
    selectedCustomer,
    selectedProduct,
    handleCustomerChange,
    handleProductChange,
    availableProducts,
    selectedPalletCustomer,
    selectedPalletProduct,
    handlePalletCustomerChange,
    handlePalletProductChange,
    availablePalletProducts,
    selectedT1,
    handleT1Change,
    filterPlace,
  };
};
